import {finalize} from 'rxjs/operators';
import { Component, OnInit, Input } from '@angular/core';
import { Router } from '@angular/router';
import { NotifierService, DialogService } from '../../ui';
import { CLAccount } from '../client';
import { SLEntity } from './entity';
import { SLAuthorization } from './authorization';
import { SLAuthorizationFilter, SLAuthorizationService } from './authorization.service';
import { CLAccountSearchDialogComponent } from '../client/account-search-dialog.component';
import { SLEntitySearchDialogComponent } from './entity-search-dialog.component';
import { sortBy } from 'lodash';

@Component({
  selector: 'sl-authorization-list',
  templateUrl: './authorization-list.component.html',
})
export class SLAuthorizationListComponent implements OnInit {

  @Input()
  account: CLAccount

  @Input()
  entity: SLEntity

  @Input()
  title: string = ''

  filter: SLAuthorizationFilter;

  authorizations: SLAuthorization[]

  constructor(
    private authorizationService: SLAuthorizationService,
    private router: Router,
    private notifier: NotifierService,
    private dialogService: DialogService
  ) { }

  ngOnInit() {
    this.filter = new SLAuthorizationFilter();

    if (this.account) {
      this.filter.account = this.account.id;
    }

    if (this.entity) {
      this.filter.entity = this.entity.id;
    }
    
    // Hide progress bar on first load
    this.fetch(false);
  }

  private fetch(showNotification = true) {
    if (showNotification) {
      this.notifier.startProgress();
    }

    this.authorizationService
      .authorizations(this.filter).pipe(
      finalize(() => {
        if (showNotification) {
          this.notifier.endProgress();
        }
      }))
      .subscribe((result) => {
        if (this.account) {
          this.authorizations = sortBy(result, a => a.entity.name);
        }
        else if (this.entity) {
          this.authorizations = sortBy(result, a => a.account.name);
        }
      });
  }

  showCLAccount(account: CLAccount) {
    if (account.deleted) {
      this.router.navigate(['shares-ledger', 'client-accounts', 'deleted', account.id]);
    }
    else {
      this.router.navigate(['shares-ledger', 'client-accounts', account.id]);
    }
  }

  showSLEntity(entity: SLEntity) {
    if (entity.deleted) {
      this.router.navigate(['shares-ledger', 'entities', 'deleted', entity.id]);
    }
    else {
      this.router.navigate(['shares-ledger', 'entities', entity.id]);
    }
  }

  showItem(authorization: SLAuthorization, event: Event) {
    let nodeName = (<Element>event.target).nodeName;

    if (nodeName === 'TD' || nodeName === 'TR') {
      if (this.account) {
        this.showSLEntity(authorization.entity);
      }
      else if (this.entity) {
        this.showCLAccount(authorization.account);
      }
    }
  }

  add(event: Event) {

    // We have an entity
    if (this.entity) {
      this.dialogService.show({
        component: CLAccountSearchDialogComponent,
        props: {
          onClose: () => {
            this.dialogService.hide();
          },
  
          onSelect: (account: CLAccount) => {
            this.dialogService.hide();
  
            this.save(account, this.entity);
          }
        }
      });
    }
    // We have an account
    else if (this.account) {
      this.dialogService.show({
        component: SLEntitySearchDialogComponent,
        props: {
          onClose: () => {
            this.dialogService.hide();
          },
  
          onSelect: (entity: SLEntity) => {
            this.dialogService.hide();
  
            this.save(this.account, entity);
          }
        }
      });
    }

    event.preventDefault();
  }

  save(account: CLAccount, entity: SLEntity) {
    const authorization = new SLAuthorization({
      account,
      entity,
      type: 'FULL',
    });

    if (!authorization.valid) {
      return;
    }

    let notifier = this.notifier;

    notifier.updateProgress(20);

    function onComplete() {
      notifier.endProgress();
    }

    this.authorizationService
      .create(authorization).pipe(
      finalize(onComplete))
      .subscribe((authorization) => {
        this.notifier.success('Authorization ajoutée avec succès');

        this.fetch();
      }, (err) => {
        this.notifier.endProgress();
      });
  }

  delete(authorization: SLAuthorization) {
    this.dialogService
      .confirmDelete('Voulez-vous supprimer cette autorisation?')
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.authorizationService
          .delete(authorization.id)
          .subscribe((id) => {
            this.notifier.success('Autorisation supprimée avec succès');

            this.fetch();
          }, (err) => {
            this.notifier.endProgress();
          });
      });
    
    return false;
  }
}
